'use client';

import { Avatar } from '@common/components';
import { SvgGrid } from '@common/icon';
import { useCallback, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { BREAKPOINTS, ROUTES } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';

import { HeaderActionPopover } from './HeaderActionPopover';
import { ServiceShortcutFullScreenModal } from './ServiceShortcutMenu';
import { ServiceShortcutPopoverContent } from './ServiceShortcutPopoverContent';

const MENU_POPOVER_CONTENT_CLASS = 'w-[330px] overflow-hidden pt-20 pr-[5px]';
const MENU_POPOVER_SCROLL_CLASS =
  'max-h-[780px] overflow-y-auto pt-0 pb-20 [&::-webkit-scrollbar]:w-6 [&::-webkit-scrollbar-thumb]:rounded-pill [&::-webkit-scrollbar-thumb]:bg-Gray-300 [&::-webkit-scrollbar-track]:bg-transparent';

interface HeaderProfileAvatarProps {
  showProfileAvatar?: boolean;
}

export const HeaderProfileAvatar = ({
  showProfileAvatar = true,
}: HeaderProfileAvatarProps) => {
  const myInfo = useAuthStore((state) => state.myInfo);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobileTablet = useMediaQuery(BREAKPOINTS.MOBILE_TABLET);

  const handleMenuChange = useCallback((open: boolean) => {
    setMenuOpen(open);
  }, []);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const openMenu = useCallback(
    () => handleMenuChange(true),
    [handleMenuChange],
  );

  const menuTriggerButton = (
    <button
      type="button"
      aria-label="전체 메뉴"
      aria-expanded={menuOpen}
      onClick={isMobileTablet ? openMenu : undefined}
      className="flex size-40 !w-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"
    >
      <SvgGrid className="size-24" />
    </button>
  );

  return (
    <>
      {isMobileTablet ? (
        <>
          {menuTriggerButton}
          <ServiceShortcutFullScreenModal open={menuOpen} onClose={closeMenu} />
        </>
      ) : (
        <HeaderActionPopover
          open={menuOpen}
          onOpenChange={handleMenuChange}
          contentClassName={MENU_POPOVER_CONTENT_CLASS}
          maxFloatingHeight={800}
          trigger={menuTriggerButton}
        >
          <ServiceShortcutPopoverContent
            onNavigate={closeMenu}
            className={MENU_POPOVER_SCROLL_CLASS}
          />
        </HeaderActionPopover>
      )}

      {showProfileAvatar && (
        <CustomLink
          href={ROUTES.MY.pathname}
          aria-label="마이페이지"
          className="flex size-40 shrink-0 items-center justify-center p-0 transition-opacity hover:opacity-80"
        >
          <Avatar size={32} src={myInfo?.profileImage} alt="내 프로필" />
        </CustomLink>
      )}
    </>
  );
};
